import React, { useState, useRef, useEffect, useCallback } from "react";
import AiListeningVideo from "../assets/AI_Listening.mp4";
import AiSpeakingVideo from "../assets/AI_Speaking.mp4";
import AiListeningFemaleVideo from "../assets/AI_ListeningFemale.mp4";
import AiSpeakingFemaleVideo from "../assets/AI_SpeakingFemale.mp4";
import avatarStreamService from "../utils/AvatarStreamService";
import CustomAvatarDisplay from "./CustomAvatarDisplay";
import TwoDAvatar from "./TwoDAvatar";

const STATIC_AVATAR_VIDEOS = {
  male: { listening: AiListeningVideo, speaking: AiSpeakingVideo },
  female: { listening: AiListeningFemaleVideo, speaking: AiSpeakingFemaleVideo },
};

const normalizeAvatarGender = (gender) =>
  gender === "female" || gender === "male" ? gender : null;

// ─── Speak a string using Web Speech API ────────────────────────────────────
const speakText = (text, locale = "en-US") => {
  if (!window.speechSynthesis) return;

  // Cancel any ongoing speech before speaking the next step
  window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(text);

  utterance.lang = locale;

  const pickVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    const languageCode = locale.split("-")[0].toLowerCase();
    return voices.find((voice) =>
      (voice.lang || "").toLowerCase() === locale.toLowerCase(),
    ) || voices.find((voice) =>
      (voice.lang || "").toLowerCase().split("-")[0] === languageCode,
    ) || null;
  };

  const voice = pickVoice();
  if (voice) utterance.voice = voice;

  utterance.rate = 0.95; // Slightly slower — calm & professional
  utterance.pitch = 1.05; // Slightly warm
  utterance.volume = 0.9;

  window.speechSynthesis.speak(utterance);
};

// ─── Animated filler shown while D-ID WebRTC is connecting ───────────────────
const ENGLISH_FILLER_STEPS = [
  { icon: "🔗", label: "Connecting to interview server" },
  { icon: "🤝", label: "Establishing secure WebRTC channel" },
  { icon: "🎙️", label: "Warming up your AI interviewer" },
  { icon: "🧠", label: "Loading interview context and questions" },
  { icon: "🎬", label: "Preparing your avatar stream. Almost there!" },
];

const INDONESIAN_FILLER_STEPS = [
  { icon: "🔗", label: "Menghubungkan ke server wawancara" },
  { icon: "🤝", label: "Membangun saluran WebRTC yang aman" },
  { icon: "🎙️", label: "Menyiapkan pewawancara AI" },
  { icon: "🧠", label: "Memuat konteks dan pertanyaan wawancara" },
  { icon: "🎬", label: "Menyiapkan avatar Anda" },
];

const DIDConnectingFiller = ({ streamReady, languageLocale }) => {
  const steps = languageLocale === "id-ID"
    ? INDONESIAN_FILLER_STEPS
    : ENGLISH_FILLER_STEPS;

  const [visibleCount, setVisibleCount] = useState(0);
  const [done, setDone] = useState(false);

  // Stagger bullet points appearing one by one + speak each label aloud
  useEffect(() => {
    if (streamReady) {
      setDone(true);
      return;
    }
    if (visibleCount >= steps.length) return;
    const t = setTimeout(() => {
      setVisibleCount((c) => {
        const next = c + 1;
        // Speak the label of the step that just appeared (0-indexed → next-1)
        speakText(steps[next - 1].label, languageLocale);
        return next;
      });
    }, 1800);
    return () => clearTimeout(t);
  }, [visibleCount, streamReady, steps, languageLocale]);

  // When stream is ready, mark done + stop any ongoing TTS
  useEffect(() => {
    if (streamReady) {
      window.speechSynthesis?.cancel();
      setDone(true);
    }
  }, [streamReady]);

  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center px-8"
      style={{
        background:
          "radial-gradient(ellipse at 60% 30%, #1a0a3a 0%, #0d0d1a 60%, #000 100%)",
        opacity: done ? 0 : 1,
        transition: "opacity 0.7s ease",
        pointerEvents: done ? "none" : "auto",
      }}
    >
      {/* Animated logo ring */}
      <div className="relative mb-8 flex items-center justify-center">
        {/* Outer pulse ring */}
        <div
          className="absolute w-28 h-28 rounded-full border border-violet-500/30"
          style={{ animation: "ping 2s cubic-bezier(0,0,0.2,1) infinite" }}
        />
        {/* Mid ring */}
        <div
          className="absolute w-20 h-20 rounded-full border border-violet-400/40"
          style={{ animation: "ping 2s cubic-bezier(0,0,0.2,1) infinite 0.4s" }}
        />
        {/* Core icon */}
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center"
          style={{
            background:
              "linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%)",
            boxShadow: "0 0 40px rgba(124,58,237,0.5)",
          }}
        >
          <span className="text-2xl">🤖</span>
        </div>
      </div>

      {/* Title */}
      <h2
        className="text-white font-bold text-xl mb-1 tracking-wide"
        style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.08em" }}
      >
        Viriqo Hires
      </h2>
      <p className="text-violet-300 text-sm mb-8 opacity-70">
        Your AI Interview is being prepared
      </p>

      {/* Bullet steps */}
      <div className="w-full max-w-xs space-y-3">
        {steps.slice(0, visibleCount).map((step, i) => (
          <div
            key={i}
            className="flex items-center gap-3"
            style={{
              animation: "fadeSlideIn 0.4s ease both",
            }}
          >
            {/* Done checkmark or spinner */}
            {i < visibleCount - 1 ? (
              <span className="w-5 h-5 rounded-full bg-violet-600/70 flex items-center justify-center text-xs text-white flex-shrink-0">
                ✓
              </span>
            ) : (
              <span className="w-5 h-5 flex-shrink-0">
                <span
                  className="block w-5 h-5 border-2 border-violet-400 border-t-transparent rounded-full"
                  style={{ animation: "spin 0.8s linear infinite" }}
                />
              </span>
            )}
            <span
              className="text-sm"
              style={{
                color: i < visibleCount - 1 ? "#c4b5fd" : "#ede9fe",
                fontWeight: i === visibleCount - 1 ? 600 : 400,
              }}
            >
              {step.label}
            </span>
          </div>
        ))}
      </div>

      {/* Bottom hint */}
      <p className="mt-10 text-gray-600 text-xs text-center">
        Establishing peer-to-peer connection…
      </p>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateX(-12px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes ping {
          75%, 100% { transform: scale(2); opacity: 0; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const AIPanel = ({ status, messages, languageLocale = "en-US" }) => {
  const [showTranscripts, setShowTranscripts] = useState(true);
  const [avatarMode, setAvatarMode] = useState(null); // null | 'static' | 'did' | 'custom'
  const [avatarGender, setAvatarGender] = useState("male");
  const [staticVideoMode, setStaticVideoMode] = useState("listening"); // 'listening' | 'speaking' for static mode

  // ── NEW: track whether the WebRTC stream has actually delivered a video frame ──
  // This drives the filler vs. live-video transition.
  const [streamReady, setStreamReady] = useState(false);

  // ── NEW: track D-ID speaking separately from status prop ──────────────────
  // We set this true on avatar_speaking_start and false only after
  // avatar_speaking_end fires AND a short audio-drain guard has passed.
  const [didSpeaking, setDidSpeaking] = useState(false);

  // ── NEW: track custom avatar frame data ────────────────────────────────────
  const [currentFrame, setCurrentFrame] = useState(null);
  const [frameIndex, setFrameIndex] = useState(-1);
  const [audioChunkData, setAudioChunkData] = useState(null);

  // ── NEW: track custom avatar speaking state ────────────────────────────────
  const [customSpeaking, setCustomSpeaking] = useState(false);

  const listeningVideoRef = useRef(null);
  const listeningVideoRef2 = useRef(null); // double-buffer B for seamless loop
  const speakingVideoRef = useRef(null);
  const speakingVideoRef2 = useRef(null); // double-buffer B for seamless loop
  const activeListeningBuf = useRef(0); // which buffer is on-stage: 0 or 1
  const activeSpeakingBuf = useRef(0);
  const webrtcVideoRef = useRef(null);
  const audioTapRef = useRef(null); // MediaStreamAudioSourceNode — keeps D-ID audio in recording

  // AI speaking condition
  // - static: uses staticVideoMode (driven by browser_tts events)
  // - D-ID: uses didSpeaking (driven by real audio energy detection)
  // - custom: uses customSpeaking (driven by avatar_speaking_start/end events)
  // - 2d: reacts to the same speaking lifecycle as other modes (any active speaking flag)
  const isAISpeaking =
    avatarMode === "static"
      ? staticVideoMode === "speaking"
      : avatarMode === "did"
        ? didSpeaking
        : avatarMode === "custom"
          ? customSpeaking
          : avatarMode === "2d"
            ? (staticVideoMode === "speaking" || didSpeaking || customSpeaking)
            : false;

  // ── Tap D-ID WebRTC audio into the recording mix ──────────────────────────
  // The avatar speaks through a <video> whose srcObject is a WebRTC MediaStream.
  // That audio goes straight to browser speakers and is invisible to MediaRecorder.
  // Fix: grab the audio tracks from video.srcObject, wrap in a new MediaStream,
  // create a MediaStreamAudioSourceNode, and connect it to
  // window.__AI_AUDIO_DESTINATION__ (the same destination that captures mic audio).
  // The <video> element's own speaker output is unaffected — user still hears it.
  const _tapDIDAudio = useCallback(() => {
    if (audioTapRef.current) return; // already tapped
    const destination = window.__AI_AUDIO_DESTINATION__;
    if (!destination) return; // interview not started yet — retried on next event
    const videoEl = webrtcVideoRef.current;
    if (!videoEl || !videoEl.srcObject) return;
    const audioTracks = videoEl.srcObject.getAudioTracks();
    if (audioTracks.length === 0) return; // no audio tracks yet — retried on canplay
    try {
      const audioCtx = destination.context;
      if (audioCtx.state === "suspended") audioCtx.resume();
      const source = audioCtx.createMediaStreamSource(
        new MediaStream(audioTracks),
      );
      source.connect(destination); // → into recording mix
      // NOTE: do NOT connect to audioCtx.destination here — the video element
      // already outputs to speakers itself (muted=false), so connecting the
      // source node too would cause an echo.
      audioTapRef.current = source;
      console.log("✅ [AIPanel] D-ID audio tapped into recording mix");
    } catch (e) {
      console.warn("⚠️ [AIPanel] Audio tap failed:", e.message);
    }
  }, []);

  // ── Callback ref — registers DOM node with service the instant it mounts ──
  const webrtcVideoCallbackRef = useCallback((node) => {
    webrtcVideoRef.current = node;
    if (node) {
      console.log(
        "🎬 [AIPanel] WebRTC video element mounted — registering immediately",
      );
      avatarStreamService.videoElement = node;
    }
  }, []);

  // ── Detect first real video frame to hide the filler overlay ─────────────
  const handleVideoCanPlay = useCallback(() => {
    console.log("🎬 [AIPanel] WebRTC video canplay — stream is ready");
    setStreamReady(true);
    _tapDIDAudio(); // srcObject is set by now — attempt tap
  }, [_tapDIDAudio]);

  // Also handle the 'loadeddata' event as a backup trigger
  const handleVideoLoadedData = useCallback(() => {
    setStreamReady(true);
    _tapDIDAudio();
  }, [_tapDIDAudio]);

  // ── Handle custom avatar frames ────────────────────────────────────────────
  const handleCustomAvatarFrame = useCallback(({ frameData, frameIndex }) => {
    console.log("🎬 [FIX] Frame received in AIPanel:", frameIndex);
    setCurrentFrame(frameData);
    setFrameIndex(frameIndex);
  }, []);

  // ── Handle custom avatar audio chunks ──────────────────────────────────────
  const handleCustomAvatarAudio = useCallback(({ audioData, timestamp }) => {
    // Wrap in object with timestamp so React always sees a new value
    // even if two consecutive chunks have identical base64 data
    setAudioChunkData({ data: audioData, ts: timestamp });
  }, []);

  // ── Fetch avatar mode from backend on mount ───────────────────────────────
  useEffect(() => {
    const fetchAvatarMode = async () => {
      try {
        const API_BASE =
          process.env.REACT_APP_REST_BASE_URL || "http://localhost:8000";
        const response = await fetch(`${API_BASE}/avatar/current-avatar`);
        if (!response.ok) throw new Error(`Status ${response.status}`);
        const data = await response.json();
        const mode = data.avatar_mode || "static";
        const gender = normalizeAvatarGender(data.avatar_gender) || "male";
        console.log("🎬 [AIPanel] Backend avatar configuration:", mode, gender);
        setAvatarMode(mode);
        setAvatarGender(gender);
      } catch (error) {
        console.error("❌ [AIPanel] Failed to fetch avatar mode:", error);
        // Fall back to waiting for mode-change from service
        setAvatarMode(null);
      }
    };
    fetchAvatarMode();
  }, []);

  // ── Initialize service + subscribe to events ──────────────────────────────
  useEffect(() => {
    avatarStreamService
      .initialize(undefined, webrtcVideoRef.current)
      .catch((e) => console.warn("Error initializing AvatarStreamService:", e));

    const handleModeChange = (modeData) => {
      console.log("🎬 [AIPanel] mode-changed:", modeData);
      const nextGender = normalizeAvatarGender(
        modeData?.avatar_gender || modeData?.gender,
      );
      if (nextGender) {
        setAvatarGender(nextGender);
      }
      if (modeData.mode === "did") {
        setAvatarMode("did");
      } else if (modeData.mode === "static") {
        setAvatarMode("static");
        setStaticVideoMode(
          modeData.type === "speaking" || modeData.type === "listening"
            ? modeData.type
            : "listening",
        );
      } else if (modeData.mode === "custom") {
        setAvatarMode("custom");
      }
    };

    // ── Custom avatar speaking state ──────────────────────────────────────
    const handleCustomSpeakingStart = (e) => {
      if (e?.detail?.mode === "custom" || avatarMode === "custom") {
        console.log("🎬 [AIPanel] custom avatar speaking START");
        setCustomSpeaking(true);
      }
    };
    const handleCustomSpeakingEnd = () => {
      console.log("🎬 [AIPanel] custom avatar speaking END");
      setTimeout(() => setCustomSpeaking(false), 300);
    };
    window.addEventListener("avatar_speaking_start", handleCustomSpeakingStart);
    window.addEventListener("avatar_speaking_end", handleCustomSpeakingEnd);

    // ── Handle browser TTS events for static mode ─────────────────────────
    const syncGenderFromTTSEvent = (event) => {
      const nextGender = normalizeAvatarGender(
        event?.detail?.avatar_gender || event?.detail?.gender,
      );
      if (nextGender) {
        setAvatarGender(nextGender);
      }
    };

    const handleTTSStart = (event) => {
      syncGenderFromTTSEvent(event);
      console.log("🎬 TTS START → speaking video");
      setStaticVideoMode("speaking");
    };

    const handleTTSEnd = (event) => {
      syncGenderFromTTSEvent(event);
      console.log("🎬 TTS END → listening video");
      setStaticVideoMode("listening");
    };

    window.addEventListener("browser_tts_start", handleTTSStart);
    window.addEventListener("browser_tts_end", handleTTSEnd);

    // ── NEW: Handle avatar_speaking_start / avatar_speaking_end ──────────
    // avatar_speaking_start = server done, audio in transit (don't show badge yet)
    // did_audio_actually_playing = audio energy confirmed on stream (show badge now)
    // avatar_speaking_end = server done sending (badge stays until real silence)
    const handleActualAudioPlaying = () => {
      console.log(
        "🎬 [AIPanel] did_audio_actually_playing → didSpeaking = true",
      );
      setDidSpeaking(true);
      _tapDIDAudio(); // audio is definitely flowing — last chance to tap if missed earlier
    };

    const handleAvatarSpeakingEnd = () => {
      // AIPanel uses its own 800ms guard — the real transition is driven by
      // did-audio-silent in VoiceInterview. Here we just give a visual grace period.
      console.log(
        "🎬 [AIPanel] avatar_speaking_end received (audio may still play)",
      );
      // Don't set didSpeaking=false here — wait for silence confirmation
    };

    const handleAvatarSilent = () => {
      // Fired by VoiceInterview when did-audio-silent + speaking_end both confirmed
      console.log("🎬 [AIPanel] did_audio_silent → didSpeaking = false");
      setTimeout(() => setDidSpeaking(false), 200);
    };

    const handleWebRTCFailed = (error) => {
      console.error("🚨 [AIPanel] WebRTC failed:", error);
      setAvatarMode("audio-only");
    };

    avatarStreamService.on("mode-changed", handleModeChange);
    avatarStreamService.on("webrtc-failed", handleWebRTCFailed);
    avatarStreamService.on("custom-avatar-frame", handleCustomAvatarFrame);
    avatarStreamService.on("custom-avatar-audio", handleCustomAvatarAudio);

    window.addEventListener(
      "did_audio_actually_playing",
      handleActualAudioPlaying,
    );
    window.addEventListener("avatar_speaking_end", handleAvatarSpeakingEnd);
    window.addEventListener("did_audio_silent", handleAvatarSilent);

    return () => {
      avatarStreamService.removeListener("mode-changed", handleModeChange);
      avatarStreamService.removeListener("webrtc-failed", handleWebRTCFailed);
      avatarStreamService.removeListener(
        "custom-avatar-frame",
        handleCustomAvatarFrame,
      );
      avatarStreamService.removeListener(
        "custom-avatar-audio",
        handleCustomAvatarAudio,
      );
      window.removeEventListener(
        "did_audio_actually_playing",
        handleActualAudioPlaying,
      );
      window.removeEventListener(
        "avatar_speaking_end",
        handleAvatarSpeakingEnd,
      );
      window.removeEventListener("did_audio_silent", handleAvatarSilent);
      window.removeEventListener(
        "avatar_speaking_start",
        handleCustomSpeakingStart,
      );
      window.removeEventListener(
        "avatar_speaking_end",
        handleCustomSpeakingEnd,
      );
      window.removeEventListener("browser_tts_start", handleTTSStart);
      window.removeEventListener("browser_tts_end", handleTTSEnd);
      if (audioTapRef.current) {
        try {
          audioTapRef.current.disconnect();
        } catch (e) {}
        audioTapRef.current = null;
      }
    };
  }, [handleCustomAvatarFrame]);

  // ── Static mode video loops ───────────────────────────────────────────────
  // Static videos are mounted ONLY when avatarMode === "static".
  // In 2D mode they are not rendered or initialized at all.
  useEffect(() => {
    if (avatarMode !== "static") return; // Only initialize static videos when in static mode

    let alive = true;

    const allRefs = [
      listeningVideoRef,
      listeningVideoRef2,
      speakingVideoRef,
      speakingVideoRef2,
    ];

    // Play all immediately
    allRefs.forEach((r) => {
      const el = r.current;
      if (!el) return;
      el.currentTime = 0;
      el.play().catch(() => {});
    });

    // If the browser auto-pauses a video, immediately resume it
    const onPause = (e) => {
      if (alive) e.target.play().catch(() => {});
    };
    allRefs.forEach((r) => r.current?.addEventListener("pause", onPause));

    // Per-pair seamless swap setup
    const setupPair = (refA, refB, activeIdx) => {
      const refs = [refA, refB];
      let swapped = false;

      if (refA.current) refA.current.style.zIndex = "2";
      if (refB.current) refB.current.style.zIndex = "1";

      const handle = () => {
        const idx = activeIdx.current;
        const active = refs[idx].current;
        const standby = refs[1 - idx].current;
        if (!active || !standby || !active.duration) return;

        const timeLeft = active.duration - active.currentTime;
        if (timeLeft > 0.5) {
          swapped = false;
          return;
        }
        if (timeLeft > 0.2 || swapped) return;

        swapped = true;
        activeIdx.current = 1 - idx;
        standby.style.zIndex = "2";
        active.style.zIndex = "1";
        requestAnimationFrame(() => {
          active.currentTime = 0;
        });
      };

      refA.current?.addEventListener("timeupdate", handle);
      refB.current?.addEventListener("timeupdate", handle);

      return () => {
        refA.current?.removeEventListener("timeupdate", handle);
        refB.current?.removeEventListener("timeupdate", handle);
      };
    };

    const cleanL = setupPair(
      listeningVideoRef,
      listeningVideoRef2,
      activeListeningBuf,
    );
    const cleanS = setupPair(
      speakingVideoRef,
      speakingVideoRef2,
      activeSpeakingBuf,
    );

    return () => {
      alive = false;
      cleanL();
      cleanS();
      allRefs.forEach((r) => r.current?.removeEventListener("pause", onPause));
      // Never pause the videos themselves
    };
  }, [avatarMode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    console.log("🎬 [AIPanel] Avatar mode changed:", avatarMode);
  }, [avatarMode]);

  const staticAvatarVideos = STATIC_AVATAR_VIDEOS[avatarGender] || STATIC_AVATAR_VIDEOS.male;

  // Gender is read at interview start. If it changes before a new static
  // interview renders, refresh both existing buffers with the matching assets.
  useEffect(() => {
    if (avatarMode !== "static") return; // only refresh when static videos are active
    [listeningVideoRef, listeningVideoRef2, speakingVideoRef, speakingVideoRef2].forEach((ref) => {
      if (!ref.current) return;
      ref.current.load();
      ref.current.play().catch(() => {});
    });
  }, [avatarGender, avatarMode]);

  // ── Debug frame index changes ──────────────────────────────────────────────
  useEffect(() => {
    console.log("🎬 [AIPanel] Frame received:", frameIndex);
  }, [frameIndex]);

  // ── Debug frame data length ────────────────────────────────────────────────
  useEffect(() => {
    console.log("🧠 FRAME DATA LENGTH:", currentFrame?.length);
  }, [currentFrame]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="w-full h-full flex flex-col bg-gradient-to-br from-gray-900 via-black to-gray-800 overflow-hidden">
        {/* ── Video section ── */}
        <div
          className="overflow-hidden flex-shrink-0"
          style={{ height: "50%" }}
        >
          <div className="relative w-full h-full bg-black">
            {/* ════════════════════════════════════════
                D-ID WEBRTC MODE
            ════════════════════════════════════════ */}
            {avatarMode === "did" && (
              <>
                {/* Filler overlay — visible until streamReady */}
                <DIDConnectingFiller streamReady={streamReady} languageLocale={languageLocale} />

                {/* WebRTC video — always mounted so the ref is registered
                    immediately; hidden (opacity-0) until streamReady. */}
                <video
                  ref={webrtcVideoCallbackRef}
                  autoPlay
                  playsInline
                  muted={false}
                  onCanPlay={handleVideoCanPlay}
                  onLoadedData={handleVideoLoadedData}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{
                    display: "block",
                    opacity: streamReady ? 1 : 0,
                    transition: "opacity 0.7s ease",
                    backgroundColor: "#000",
                  }}
                />

                {/* Speaking indicator badge removed — status pill below handles this */}
              </>
            )}

            {/* ════════════════════════════════════════
                STATIC MODE VIDEOS
                Always in the DOM so refs are valid
                on mount. zIndex controls visibility.
            ════════════════════════════════════════ */}

            {/* STATIC MODE VIDEOS — only render when static mode is selected */}
            {avatarMode === "static" && (
              <>
                {/* Listening pair */}
                <div
                  className="absolute inset-0"
                  style={{
                    zIndex: !isAISpeaking ? 10 : 0,
                    pointerEvents: "none",
                  }}
                >
                  <video
                    ref={listeningVideoRef}
                    src={staticAvatarVideos.listening}
                    muted
                    playsInline
                    preload="auto"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ zIndex: 2 }}
                  />
                  <video
                    ref={listeningVideoRef2}
                    src={staticAvatarVideos.listening}
                    muted
                    playsInline
                    preload="auto"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ zIndex: 1 }}
                  />
                </div>

                {/* Speaking pair */}
                <div
                  className="absolute inset-0"
                  style={{
                    zIndex: isAISpeaking ? 10 : 0,
                    pointerEvents: "none",
                  }}
                >
                  <video
                    ref={speakingVideoRef}
                    src={staticAvatarVideos.speaking}
                    muted
                    playsInline
                    preload="auto"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ zIndex: 2 }}
                  />
                  <video
                    ref={speakingVideoRef2}
                    src={staticAvatarVideos.speaking}
                    muted
                    playsInline
                    preload="auto"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ zIndex: 1 }}
                  />
                </div>
              </>
            )}


            {avatarMode === "2d" && (
              <div
                className="absolute inset-0"
                style={{ zIndex: avatarMode === "2d" ? 10 : 0, pointerEvents: "none" }}
              >
                <TwoDAvatar gender={avatarGender} isAISpeaking={isAISpeaking} />
              </div>
            )}

            {/* ════════════════════════════════════════
                CUSTOM AVATAR MODE
            ════════════════════════════════════════ */}
            {avatarMode === "custom" && (
              <CustomAvatarDisplay
                frameData={currentFrame}
                frameIndex={frameIndex}
                isAISpeaking={isAISpeaking}
                audioChunkData={audioChunkData}
                avatarImageUrl={`${process.env.REACT_APP_AVATAR_SERVICE_URL || "http://45.194.91.156:8001"}/avatar`}
              />
            )}

            {/* ════════════════════════════════════════
                AUDIO-ONLY FALLBACK
            ════════════════════════════════════════ */}
            {avatarMode === "audio-only" && (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-800">
                <div className="flex flex-col items-center gap-6">
                  <div className="w-20 h-20 bg-purple-900/40 rounded-full flex items-center justify-center animate-pulse">
                    <svg
                      className="w-10 h-10 text-purple-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M8 16.5a6 6 0 01-6-6V9m0-4a6 6 0 0112 0v2.5m0 0a6 6 0 01-6 6m6-6V9m0 0a3 3 0 11-6 0m0 0V3m0 9a3 3 0 111 0" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <p className="text-purple-300 font-semibold mb-2">
                      🎤 Audio Mode
                    </p>
                    <p className="text-gray-400 text-sm max-w-xs">
                      WebRTC unavailable. Listening to audio response…
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════
                INITIAL LOADING (mode unknown)
            ════════════════════════════════════════ */}
            {avatarMode === null && (
              <div className="absolute inset-0 flex items-center justify-center bg-black">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-4 border-gray-700 border-t-purple-500 rounded-full animate-spin" />
                  <p className="text-gray-400 text-sm">Loading avatar…</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Bottom info bar ── */}
        <div className="flex flex-col items-center py-3 px-4 gap-3 flex-shrink-0 bg-gradient-to-t from-black/80 to-transparent">
          {/* Top Row */}
          <div className="flex items-center justify-center gap-3">
            <h2 className="text-lg font-semibold tracking-wide text-white">
            Viriqo Hires
            </h2>

            {/* Avatar Mode */}
            {/* <span
              className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${
                avatarMode === "did"
                  ? "bg-blue-900/60 text-blue-300"
                  : avatarMode === "custom"
                    ? "bg-indigo-900/60 text-indigo-300"
                    : avatarMode === "static"
                      ? "bg-gray-700/60 text-gray-300"
                      : avatarMode === "audio-only"
                        ? "bg-orange-900/60 text-orange-300"
                        : "bg-yellow-900/60 text-yellow-300"
              }`}
            >
              {avatarMode === "did"
                ? `🎬 D-ID WebRTC${streamReady ? " ✓" : " (Connecting...)"}`
                : avatarMode === "custom"
                  ? "🖼️ Custom Avatar"
                  : avatarMode === "static"
                    ? "📹 Static Mode"
                    : avatarMode === "audio-only"
                      ? "🎤 Audio Only"
                      : "⏳ Loading..."}
            </span> */}

            {/* Interview Status */}
            <span
              className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap transition-all duration-300 ${
                status === "AI Speaking..."
                  ? "bg-purple-600/80 text-white animate-pulse"
                  : status === "Interview Starting..."
                    ? "bg-purple-800/80 text-purple-200 animate-pulse"
                    : status === "Listening"
                      ? "bg-green-500/70 text-white"
                      : status === "Disconnected" || status === "Error"
                        ? "bg-red-600/80 text-white"
                        : status === "Reconnecting"
                          ? "bg-[#4B1498]/90 text-white animate-pulse"
                          : "bg-gray-700/60 text-gray-300"
              }`}
            >
              {status}
            </span>
          </div>

          {/* Transcript Button */}
          <button
            onClick={() => setShowTranscripts(!showTranscripts)}
            className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all duration-200 ${
              showTranscripts
                ? "bg-purple-600/80 text-white"
                : "bg-gray-700/60 text-gray-300 hover:bg-gray-600/60"
            }`}
          >
            {showTranscripts ? "📖 Hide Transcript" : "💬 Show Transcript"}
          </button>
        </div>

        {/* ── Transcripts ── */}
        {showTranscripts && (
          <div className="flex-1 border-t border-purple-500/40 bg-gray-900 flex flex-col min-h-0 overflow-hidden animate-slideDown">
            {" "}
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="px-4 py-4 space-y-2">
                {messages.length === 0 ? (
                  <p className="text-center text-gray-500 text-sm py-8">
                    No transcripts yet
                  </p>
                ) : (
                  [...messages].reverse().map((msg, index) => (
                    <div key={index} className="space-y-3">
                      {msg.answer && (
                        <div className="bg-blue-900/30 border-l-4 border-blue-400 rounded-lg p-3 shadow-md">
                          <p className="text-sm font-medium text-blue-200 break-words">
                            {msg.answer}
                          </p>
                        </div>
                      )}
                      {msg.question && (
                        <div className="bg-purple-900/30 border-l-4 border-purple-400 rounded-lg p-3 shadow-md">
                          <p className="text-sm font-medium text-purple-200 break-words">
                            {msg.question}
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }

        @keyframes barBounce {
          0%,
          100% {
            transform: scaleY(0.4);
          }
          50% {
            transform: scaleY(1);
          }
        }
      `}</style>
    </>
  );
};

export default AIPanel;
